import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

import * as uf from "../../../../common/unfathomable";
import { CardFace } from "./CardFace";
import { Rich } from "./RichText";
import css from "./Unfathomable.module.css";

// ================================================================ hand
export function HandBar(props: {
  view: uf.GameView;
  artOk: boolean;
  selectable: Map<number, string>;
  selected: string[];
  onToggle: (optionId: string) => void;
}) {
  const { view, artOk, selectable, selected } = props;
  const me = view.you !== null ? view.players[view.you] : null;
  const n = view.hand.length;
  // A stable order: by skill type (the card colours' order), then value, then card id.
  const hand = view.hand.slice().sort((a, b) => {
    const da = uf.skillDef(a.def), db = uf.skillDef(b.def);
    return uf.kSkillTypes.indexOf(da.type) - uf.kSkillTypes.indexOf(db.type) || da.value - db.value || a.uid - b.uid;
  });
  // While adding cards to a check: green glow = supporting skill (adds), red = opposing / treachery (subtracts).
  const contributing = view.decision?.mine && view.decision.kind === "contribute" && view.check ? view.check.supporting : null;
  const glowOf = (def: string, pickable: boolean): "good" | "bad" | "pick" | undefined => {
    if (!contributing) return pickable ? "pick" : undefined;
    const t = uf.skillDef(def).type;
    return t !== "treachery" && contributing.includes(t) ? "good" : "bad";
  };
  return (
    <div className={`${css.hand} ${css.darkFrame}`}>
      <div className={css.handInner}>
        {me && (
          <>
            <div>
              <div className={css.handLabel}>忠诚卡</div>
              <div style={{ display: "flex", gap: 6 }}>
                {view.loyalty.map(l => <CardFace key={l.uid} kind="loyalty" id={l.type} artOk={artOk} width={80} />)}
                {me.revealed && <CardFace kind="loyalty" id={me.revealed} artOk={artOk} width={80} />}
              </div>
            </div>
            <div>
              <div className={css.handLabel}>物品与面前的卡</div>
              <div style={{ display: "flex", gap: 6 }}>
                {me.items.map(it => <CardFace key={it.def} kind="item" id={it.def} artOk={artOk} width={80}
                  style={{ opacity: uf.kItemById[it.def].improvement && !it.faceUp ? 0.5 : 1 }} />)}
                {me.playArea.map((c, i) => <CardFace key={`pa${i}`} kind={c.kind === "spell" ? "spell" : "mythos"} id={c.def} artOk={artOk} width={80} />)}
                {me.featAvailable && <CardFace kind="feat" id={me.character} artOk={artOk} width={80} />}
              </div>
            </div>
          </>
        )}
        <div className={css.handMain}>
          <div className={css.handLabel}>手牌 {n}/10</div>
          <div className={css.handCards}>
            <AnimatePresence initial={false}>
              {hand.map((c, i) => {
                const opt = selectable.get(c.uid);
                const staged = !!opt && selected.includes(opt);
                const rot = (i - (n - 1) / 2) * Math.min(4, 30 / Math.max(n, 1));
                return (
                  <motion.div key={c.uid} layout initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1, rotate: rot }}
                    exit={{ y: -120, opacity: 0, scale: 0.6 }} transition={{ type: "spring", stiffness: 260, damping: 24 }}
                    style={{
                      marginLeft: i === 0 ? 0 : n > 8 ? -44 : -22, zIndex: i, transformOrigin: "50% 120%",
                      // Unavailable cards are greyed, not see-through (they overlap their neighbours).
                      filter: opt || selectable.size === 0 ? "none" : "grayscale(0.75) brightness(0.55)",
                    }}>
                    {staged ? (
                      <div className={css.cardSlot} title="已放入出牌区" onClick={() => props.onToggle(opt!)} />
                    ) : (
                      <CardFace kind="skill" id={c.def} artOk={artOk} width={100} glow={glowOf(c.def, !!opt)} layoutId={`uf-card-${c.uid}`}
                        onClick={opt ? () => props.onToggle(opt) : undefined} />
                    )}
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}

// ================================================================ log & timeline
/**
 * "第 R 轮 · 第 T 回合": every player takes one turn per round, so the round is the turn number
 * divided by the number of players. Turn 0 is the setup before the first turn.
 */
export function roundLabel(turn: number, players: number): string {
  if (turn <= 0) return "开局准备";
  return `第 ${Math.floor((turn - 1) / Math.max(1, players)) + 1} 轮 · 第 ${turn} 回合`;
}

export function LogPanel(props: {
  view: uf.GameView;
  onProposeUndo: (decisionId: number, reshuffle: boolean) => void;
}) {
  const { view } = props;
  const [tab, setTab] = useState<"log" | "timeline">("log");
  const [reshuffle, setReshuffle] = useState(true);
  const [showFlavor, setShowFlavor] = useState(() => localStorage.getItem("unfathomable-flavor") !== "off");
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { if (tab === "log" && ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [view.log.length, tab]);
  return (
    <div className={css.darkFrame} style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <div className={css.tabs}>
        <button className={`${css.tab} ${tab === "log" ? css.tabActive : ""}`} onClick={() => setTab("log")}>航海日志</button>
        <button className={`${css.tab} ${tab === "timeline" ? css.tabActive : ""}`} onClick={() => setTab("timeline")}>时间线 / 回退</button>
        <div style={{ flex: 1 }} />
        <label style={{ fontSize: "0.8em", color: "var(--parchment-3)", alignSelf: "center" }}>
          <input type="checkbox" checked={showFlavor} onChange={e => { setShowFlavor(e.target.checked); localStorage.setItem("unfathomable-flavor", e.target.checked ? "on" : "off"); }} /> 旁白
        </label>
      </div>
      {tab === "log" ? (
        <div className={css.log} ref={ref}>
          {view.log.filter(l => showFlavor || !l.flavor).map(l => (
            <div key={l.seq} className={`${css.logLine} ${l.to ? css.logPrivate : ""} ${l.flavor ? css.logFlavor : ""} ${l.text.startsWith("════") ? css.logTurn : ""}`}>
              {l.to ? "🔒 " : ""}<Rich text={l.text} />
            </div>
          ))}
        </div>
      ) : (
        <div className={css.log}>
          <label style={{ display: "block", marginBottom: 6 }}>
            <input type="checkbox" checked={reshuffle} onChange={e => setReshuffle(e.target.checked)} /> 回退后重新洗混面朝下的牌堆并更换随机种子（避免「预知未来」）
          </label>
          <div className={css.waiting} style={{ color: "var(--parchment-3)" }}>选择一个步骤回到它尚未作答时的状态；需要所有在线玩家同意。</div>
          {[...view.timeline].reverse().map(t => (
            <div key={t.decisionId} className={css.logLine} style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <span style={{ flex: 1 }}>
                <b>{roundLabel(t.turnNumber, view.players.length)}</b>
                {t.turnNumber > 0 && `（${view.players[(view.firstPlayer + t.turnNumber - 1) % view.players.length]?.name ?? ""}）`} · {t.text}
              </span>
              <button className={`${css.btn} ${css.btnSmall} ${css.btnGhost}`} onClick={() => props.onProposeUndo(t.decisionId, reshuffle)}>回到此处</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
